import type { OkBody } from '../Models/ApiModel';
import type { PaymentRequestModel, PublicIntakeFormModel } from '../Models/PublicModel';
import { http } from './httpService';

/**
 * The two calls made without a session.
 *
 * `anonymous` matters: without it the client would wait on a Firebase token
 * that will never arrive, and a parent opening a link from a chat would sit
 * looking at a spinner.
 */
export const PublicService = {
  paymentRequest: (token: string) =>
    http.get<PaymentRequestModel>(
      `/payment-requests/public/${encodeURIComponent(token)}`,
      { anonymous: true }
    ),

  intakeForm: (token: string) =>
    http.get<PublicIntakeFormModel>(
      `/intake-forms/public/${encodeURIComponent(token)}`,
      { anonymous: true }
    ),

  submitIntakeForm: (token: string, submission: Record<string, unknown>) =>
    http.post<OkBody>(
      `/intake-forms/public/${encodeURIComponent(token)}`,
      submission,
      { anonymous: true }
    )
};

/** The tutor's own side of both documents. Authenticated and premium. */
export const DocumentService = {
  createPaymentRequest: (student: string, lessonIds: number[], recipientName?: string) =>
    http.post<{ token: string; total: number; issuedAt: string }>('/payment-requests', {
      student,
      lessonIds,
      ...(recipientName ? { recipientName } : {})
    }),

  listRequests: (student: string) =>
    http.get<{ requests: { token: string; createdAt: string; total: number; viewedAt: string | null; viewCount: number }[] }>(
      `/payment-requests/student/${encodeURIComponent(student)}`
    ),

  createIntakeForm: (label?: string, note?: string) =>
    http.post<{ token: string }>('/intake-forms', { label, note }),

  listIntakeForms: () =>
    http.get<{ forms: { token: string; label: string | null; note: string | null; createdAt: string; open: boolean; submissionCount: number }[]; ttlDays: number; maxSubmissions: number }>(
      '/intake-forms'
    ),

  /**
   * What came in through the links. Each card is the raw payload the client
   * filled in, kept as it arrived rather than merged into a student, because
   * accepting one is the tutor's decision and not the form's.
   */
  listIntakeSubmissions: () =>
    http.get<{ submissions: { id: number; student: string; createdAt: string; payload: Record<string, unknown> | null }[] }>(
      '/intake-forms/submissions'
    ),

  revokeIntakeForm: (token: string) => http.post<OkBody>(`/intake-forms/${token}/revoke`),

  deleteIntakeForm: (token: string) => http.delete<OkBody>(`/intake-forms/${token}`)
};
